'use client';

import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { Form } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { useUsernameUpdate } from '@sola/my-sdk/settings/hooks.ts';
import type { UserError } from '@sola/shared-utils/errors-user.ts';

import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import {
  createUsernameUpdateErrorAlert,
  createUsernameUpdateSuccessAlert,
} from '../../utils/alerts.tsx';

export const SettingsModalUsername = ({
  username,
  openTrigger,
}: {
  username: string;
  openTrigger: React.ReactElement<HTMLButtonElement>;
}) => {
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const [isOpen, setIsOpen] = useState(false);

  const { mutateAsync: updateUsername } = useUsernameUpdate();
  const defaultFormValues = {
    username: username,
  };

  const { t } = useTranslation(['core', 'my-settings']);

  const handleSubmit = async (data: FieldValues) => {
    clearAlerts();
    try {
      await updateUsername({
        preferredUsername: data.username as string,
      });

      addAlerts(createUsernameUpdateSuccessAlert());
      setIsOpen(false);
    } catch (error) {
      const errorType = (error as UserError)?.type;
      addAlerts(createUsernameUpdateErrorAlert(errorType));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>{openTrigger}</DialogTrigger>
      <DialogContent
        icon="user-edit"
        title={t('my-settings:form.username.title')}
      >
        <Form
          onSubmit={handleSubmit}
          options={{
            defaultValues: defaultFormValues,
          }}
        >
          <InputField
            key="username"
            name="username"
            label={t('my-settings:form.username.usernameLabel')}
            required
          />
          <DialogActions>
            <DialogActionClose>{t('core:actions.cancel')}</DialogActionClose>
            <DialogActionPrimary type="submit">
              {t('core:actions.save')}
            </DialogActionPrimary>
          </DialogActions>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
